/**
 * @fileoverview Flight vs Crew Compare
 * @description Reads flights from a source Service Bus connection and verifies,
 *              for each flight, that crew data exists on a target connection.
 *              Connections are picked from the Service Bus Explorer saved
 *              connections. Results are summarized in cards, with each flight
 *              shown as a card that flags the "no crew" issue case.
 */

import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box, Paper, TextField, Button, Typography, Stack, Alert, Chip,
  CircularProgress, Fade, Grow, Divider, IconButton, Collapse, Tooltip,
  Autocomplete, ToggleButton, ToggleButtonGroup, Grid,
} from '@mui/material';
import {
  GitCompareArrows, Plane, Users, AlertTriangle, CheckCircle2, Play,
  RefreshCw, ChevronDown, ChevronUp, Link2, Settings2, Layers, Search, Sparkles,
} from 'lucide-react';
import { serviceBusExplorerApi } from '@/features/ServiceBusExplorer/services/serviceBusExplorerApi';
import { flightCrewCompareApi } from '../services/flightCrewCompareApi';

/* ── Shared sx tokens ── */
const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px', bgcolor: '#fff',
    '&.Mui-focused': { boxShadow: '0 0 0 3px rgba(0,151,169,0.10)' },
  },
  '& .MuiInputLabel-root.Mui-focused': { color: '#0097A9' },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#0097A9', borderWidth: 2,
  },
};

const toggleSx = {
  '& .MuiToggleButton-root': {
    textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
    borderRadius: '8px', px: 1.75, py: 0.6,
    '&.Mui-selected': { bgcolor: '#0097A9', color: '#fff', '&:hover': { bgcolor: '#007f8f' } },
  },
};

const DEFAULT_FIELDS = {
  carrier_path: 'flights.carrier',
  flight_number_path: 'flights.flightNumber',
  udor_path: 'flights.udor',
  crew_id_path: 'eventData.assignedCrew.crewId',
  target_carrier_path: 'eventData.carrier',
  target_flight_number_path: 'eventData.flightNumber',
  target_udor_path: 'eventData.udor',
};

/* Advanced mapping fields: [key, label, scope] — scope picks which sampled
   connection's field paths populate the picker (source = flights, target = crew). */
const ADVANCED_FIELDS = [
  ['carrier_path', 'Carrier path', 'source'],
  ['flight_number_path', 'Flight number path', 'source'],
  ['udor_path', 'UDOR (date) path', 'source'],
  ['crew_id_path', 'Crew ID path (target)', 'target'],
  ['target_flight_number_path', 'Target flight number path (optional)', 'target'],
  ['target_carrier_path', 'Target carrier path (optional)', 'target'],
  ['target_udor_path', 'Target UDOR path (optional)', 'target'],
];

const leafOf = (path) => (path || '').split('.').pop() || path || '';

/* ── Field path picker: an autocomplete backed by sampled message paths ── */
function PathField({ label, value, placeholder, options, onChange }) {
  return (
    <Autocomplete
      freeSolo
      options={options}
      value={value || null}
      isOptionEqualToValue={(o, v) =>
        (typeof o === 'string' ? o : o.path) === (typeof v === 'string' ? v : v?.path)
      }
      onChange={(_, val) =>
        onChange(val == null ? '' : (typeof val === 'string' ? val : val.path))
      }
      onInputChange={(_, val, reason) => { if (reason === 'input') onChange(val); }}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.path || '')}
      filterOptions={(opts, state) => {
        const q = state.inputValue.trim().toLowerCase();
        const matches = q
          ? opts.filter(
              (o) =>
                (o.path || '').toLowerCase().includes(q) ||
                leafOf(o.path).toLowerCase().includes(q) ||
                String(o.sample ?? '').toLowerCase().includes(q),
            )
          : opts;
        return matches.slice(0, 50);
      }}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.path} sx={{ display: 'block !important', py: 0.75 }}>
          <Typography sx={{ fontWeight: 600, fontSize: '0.8rem', color: '#0f172a' }}>
            {leafOf(option.path)}
          </Typography>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.66rem', color: '#64748b', display: 'block' }}>
            {option.path}
          </Typography>
          {option.sample !== undefined && option.sample !== '' && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              e.g. {String(option.sample)}
            </Typography>
          )}
        </Box>
      )}
      size="small"
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder={placeholder} size="small" sx={inputSx} />
      )}
    />
  );
}

/* ── Summary stat card ── */
function StatCard({ icon: Icon, label, value, color, bg }) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.25, borderRadius: '14px', border: '1px solid #e5e7eb',
        display: 'flex', alignItems: 'center', gap: 1.75, height: '100%',
      }}
    >
      <Box
        sx={{
          width: 44, height: 44, borderRadius: '12px', bgcolor: bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        }}
      >
        <Icon size={22} color={color} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" fontWeight={800} color={color} lineHeight={1.1}>
          {value}
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          {label}
        </Typography>
      </Box>
    </Paper>
  );
}

/* ── Per-flight result card ── */
function FlightCard({ flight, direction }) {
  const [open, setOpen] = useState(false);
  const isIssue = flight.status === 'issue';
  const isReverse = direction === 'crew_to_flight';
  const accent = isIssue ? '#dc2626' : '#00875A';
  const bg = isIssue ? '#fef2f2' : '#ecfdf5';
  const chipLabel = isReverse
    ? (isIssue ? 'No flight' : 'Flight found')
    : (isIssue ? 'No crew' : `${flight.crew_count} crew`);

  return (
    <Grow in timeout={350}>
      <Paper
        elevation={0}
        sx={{
          borderRadius: '14px', border: '1px solid #e5e7eb',
          borderLeft: `4px solid ${accent}`, overflow: 'hidden', height: '100%',
        }}
      >
        <Box sx={{ p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              <Plane size={16} color="#01426A" />
              <Typography variant="subtitle2" fontWeight={800} color="#01426A" noWrap>
                {(flight.carrier || '—')} {flight.flight_number}
              </Typography>
            </Stack>
            <Chip
              size="small"
              icon={isIssue ? <AlertTriangle size={12} /> : <CheckCircle2 size={12} />}
              label={chipLabel}
              sx={{
                bgcolor: bg, color: accent, fontWeight: 700, fontSize: '0.7rem',
                border: `1px solid ${accent}22`, '& .MuiChip-icon': { color: accent },
              }}
            />
          </Stack>

          <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
            UDOR: {flight.udor || '—'}
          </Typography>

          {flight.crew_count > 0 && (
            <>
              <Divider sx={{ my: 1.25 }} />
              <Box
                onClick={() => setOpen((o) => !o)}
                sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  cursor: 'pointer', userSelect: 'none',
                }}
              >
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Users size={14} color="#0097A9" />
                  <Typography variant="caption" fontWeight={700} color="#0097A9">
                    {flight.crew_count} distinct crew
                  </Typography>
                </Stack>
                {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              </Box>
              <Collapse in={open}>
                <Stack direction="row" flexWrap="wrap" gap={0.5} mt={1}>
                  {flight.crew_ids.map((id) => (
                    <Chip key={id} label={id} size="small" variant="outlined"
                      sx={{ fontSize: '0.68rem', height: 22 }} />
                  ))}
                </Stack>
              </Collapse>
            </>
          )}
        </Box>
      </Paper>
    </Grow>
  );
}

/* ── Connection option renderer for the Autocomplete ── */
function connLabel(c) {
  if (!c) return '';
  return c.name;
}

/* ═══════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════ */
const FlightCrewCompare = () => {
  const [connections, setConnections] = useState([]);
  const [connLoading, setConnLoading] = useState(false);

  const [source, setSource] = useState(null);
  const [target, setTarget] = useState(null);
  const [sourceTarget, setSourceTarget] = useState('active');
  const [targetTarget, setTargetTarget] = useState('active');
  const [flightLimit, setFlightLimit] = useState(50);
  const [direction, setDirection] = useState('flight_to_crew');

  const [showAdvanced, setShowAdvanced] = useState(false);
  const [fields, setFields] = useState(DEFAULT_FIELDS);
  const [targetScanLimit, setTargetScanLimit] = useState(20000);

  const [sampleOptions, setSampleOptions] = useState({ source: [], target: [] });
  const [sampling, setSampling] = useState({ source: false, target: false });
  const [sampleInfo, setSampleInfo] = useState({ source: '', target: '' });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [results, setResults] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all' | 'issues'

  /* ── Load saved connections (shared with Service Bus Explorer) ── */
  const loadConnections = useCallback(async () => {
    setConnLoading(true);
    try {
      const res = await serviceBusExplorerApi.listConnections();
      setConnections(res.connections || []);
    } catch {
      setError('Could not load saved connections. Add them in the Service Bus Explorer first.');
    } finally {
      setConnLoading(false);
    }
  }, []);

  useEffect(() => { loadConnections(); }, [loadConnections]);

  const updateField = (key, val) => setFields((prev) => ({ ...prev, [key]: val }));

  /* ── Load field paths from a sample message on the source/target connection ── */
  const loadSample = useCallback(async (scope) => {
    const conn = scope === 'source' ? source : target;
    const queueTarget = scope === 'source' ? sourceTarget : targetTarget;
    if (!conn) {
      setError(`Pick a ${scope} connection first.`);
      return;
    }
    setError('');
    setSampling((p) => ({ ...p, [scope]: true }));
    try {
      const revealed = await serviceBusExplorerApi.revealConnection(conn.id);
      if (!revealed.connection_string) {
        setError(`Could not load the ${scope} connection string.`);
        return;
      }
      const res = await serviceBusExplorerApi.sampleFields(
        revealed.connection_string,
        revealed.entity_name || conn.entity_name || '',
        // 'both' isn't a valid single target for peek; fall back to active.
        queueTarget === 'both' ? 'active' : queueTarget,
        (revealed.kind || conn.kind) === 'topic'
          ? (revealed.subscription_name || conn.subscription_name || '')
          : '',
      );
      if (res.error) {
        setSampleOptions((p) => ({ ...p, [scope]: [] }));
        setSampleInfo((p) => ({ ...p, [scope]: '' }));
        setError(res.error);
        return;
      }
      const found = res.fields || [];
      setSampleOptions((p) => ({ ...p, [scope]: found }));
      setSampleInfo((p) => ({
        ...p,
        [scope]: found.length
          ? `${found.length} field${found.length > 1 ? 's' : ''} loaded from a sample message`
          : 'No JSON fields found in the sampled message.',
      }));
    } catch (err) {
      setError(err?.message || `Failed to load ${scope} sample fields.`);
    } finally {
      setSampling((p) => ({ ...p, [scope]: false }));
    }
  }, [source, target, sourceTarget, targetTarget]);

  /* ── Run comparison ── */
  const handleRun = useCallback(async () => {
    if (!source || !target) {
      setError('Pick both a source (flights) and a target (crew) connection.');
      return;
    }
    if (source.id === target.id) {
      setError('Source and target must be different connections.');
      return;
    }

    setError('');
    setResults(null);
    setFilter('all');
    setLoading(true);

    try {
      const res = await flightCrewCompareApi.run({
        source_connection_id: source.id,
        target_connection_id: target.id,
        direction,
        source_queue_target: sourceTarget,
        target_queue_target: targetTarget,
        flight_limit: Number(flightLimit) || 50,
        target_scan_limit: Number(targetScanLimit) || 20000,
        field_config: fields,
      });
      if (res.error) {
        setError(res.error);
      } else {
        setResults(res);
      }
    } catch (err) {
      setError(err?.message || 'Comparison failed.');
    } finally {
      setLoading(false);
    }
  }, [source, target, sourceTarget, targetTarget, flightLimit, targetScanLimit, fields, direction]);

  const summary = results?.summary;
  const flights = useMemo(() => results?.flights || [], [results]);

  const visibleFlights = useMemo(
    () => (filter === 'issues' ? flights.filter((f) => f.status === 'issue') : flights),
    [flights, filter],
  );

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Fade in timeout={400}>
        <Paper
          elevation={0}
          sx={{
            p: 3, mb: 3, borderRadius: '16px', color: '#fff',
            background: 'linear-gradient(135deg, #01426A 0%, #0097A9 100%)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
            <GitCompareArrows size={28} />
            <Typography variant="h5" fontWeight={700}>
              Flight vs Crew Compare
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ opacity: 0.9 }}>
            Read flights from a source connection, then verify each flight has crew data on a
            target connection. Flights present in the source with no crew on the target are
            flagged as issues.
          </Typography>
        </Paper>
      </Fade>

      {/* Config */}
      <Fade in timeout={550}>
        <Paper elevation={0} sx={{ p: 3, mb: 3, borderRadius: '14px', border: '1px solid #e5e7eb' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
            <Typography variant="subtitle2" fontWeight={700} color="#01426A">
              <Link2 size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
              Connections & Scope
            </Typography>
            <Tooltip title="Reload saved connections">
              <span>
                <IconButton size="small" onClick={loadConnections} disabled={connLoading}
                  aria-label="Reload saved connections">
                  <RefreshCw size={15} className={connLoading ? 'spin' : ''} />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>

          {connections.length === 0 && !connLoading && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.8rem' }}>
              No saved connections found. Add connections in the <strong>Service Bus Explorer</strong>,
              then return here — they are shared between both tools.
            </Alert>
          )}

          {/* Direction: which side drives the comparison */}
          <Box mb={2}>
            <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.75}>
              <GitCompareArrows size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
              Comparison direction
            </Typography>
            <ToggleButtonGroup
              value={direction} exclusive size="small" sx={toggleSx}
              onChange={(_, v) => v && setDirection(v)}
            >
              <ToggleButton value="flight_to_crew">
                <Plane size={13} style={{ marginRight: 6 }} />
                Flight → Crew
              </ToggleButton>
              <ToggleButton value="crew_to_flight">
                <Users size={13} style={{ marginRight: 6 }} />
                Crew → Flight
              </ToggleButton>
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.75}>
              {direction === 'flight_to_crew'
                ? 'For each flight on the source, check that crew exists on the target. Issue = flight with no crew.'
                : 'For each flight that has crew on the target, check that a matching flight exists on the source. Issue = crew with no flight.'}
            </Typography>
          </Box>

          <Grid container spacing={2}>
            {/* Source */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.75}>
                <Plane size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                Source — Flights
              </Typography>
              <Autocomplete
                options={connections}
                value={source}
                onChange={(_, val) => setSource(val)}
                getOptionLabel={connLabel}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                loading={connLoading}
                size="small"
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.preview}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select flights connection" sx={inputSx} />
                )}
              />
              <ToggleButtonGroup
                value={sourceTarget} exclusive size="small" sx={{ ...toggleSx, mt: 1 }}
                onChange={(_, v) => v && setSourceTarget(v)}
              >
                <ToggleButton value="active"><Layers size={13} style={{ marginRight: 5 }} />Active</ToggleButton>
                <ToggleButton value="deadletter">Dead-letter</ToggleButton>
                <ToggleButton value="both">Both</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {/* Target */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" mb={0.75}>
                <Users size={13} style={{ verticalAlign: 'middle', marginRight: 5 }} />
                Target — Crew
              </Typography>
              <Autocomplete
                options={connections}
                value={target}
                onChange={(_, val) => setTarget(val)}
                getOptionLabel={connLabel}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                loading={connLoading}
                size="small"
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{option.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{option.preview}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} placeholder="Select crew connection" sx={inputSx} />
                )}
              />
              <ToggleButtonGroup
                value={targetTarget} exclusive size="small" sx={{ ...toggleSx, mt: 1 }}
                onChange={(_, v) => v && setTargetTarget(v)}
              >
                <ToggleButton value="active"><Layers size={13} style={{ marginRight: 5 }} />Active</ToggleButton>
                <ToggleButton value="deadletter">Dead-letter</ToggleButton>
                <ToggleButton value="both">Both</ToggleButton>
              </ToggleButtonGroup>
            </Grid>

            {/* Limit */}
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <TextField
                label="Max flights"
                type="number"
                value={flightLimit}
                onChange={(e) => setFlightLimit(e.target.value)}
                size="small"
                fullWidth
                inputProps={{ min: 1, max: 1000 }}
                sx={inputSx}
              />
            </Grid>
          </Grid>

          {/* Advanced */}
          <Box mt={2}>
            <Button
              size="small"
              onClick={() => setShowAdvanced((s) => !s)}
              startIcon={<Settings2 size={15} />}
              endIcon={showAdvanced ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
              sx={{ textTransform: 'none', color: '#0097A9', fontWeight: 600 }}
            >
              Advanced field mapping
            </Button>
            <Collapse in={showAdvanced}>
              <Box
                sx={{
                  mt: 1.5, p: 2, borderRadius: '10px',
                  bgcolor: '#f8fafc', border: '1px solid #e5e7eb',
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                  JSON dot-paths used to read each message. Array elements are matched by their
                  leaf key (e.g. <code>eventData.assignedCrew.crewId</code> reads every element of
                  <code> assignedCrew[]</code>). Leave target paths blank to reuse the source paths.
                </Typography>

                {/* Load sample fields — peeks a message from each connection */}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mb={1.5}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => loadSample('source')}
                    disabled={!source || sampling.source}
                    startIcon={sampling.source
                      ? <CircularProgress size={14} color="inherit" />
                      : <Sparkles size={14} />}
                    sx={{
                      borderColor: '#01426A', color: '#01426A', borderRadius: '8px',
                      textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                      '&:hover': { borderColor: '#012c47', bgcolor: 'rgba(1,66,106,0.04)' },
                    }}
                  >
                    {sampling.source ? 'Loading…' : 'Load source fields'}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => loadSample('target')}
                    disabled={!target || sampling.target}
                    startIcon={sampling.target
                      ? <CircularProgress size={14} color="inherit" />
                      : <Sparkles size={14} />}
                    sx={{
                      borderColor: '#0097A9', color: '#0097A9', borderRadius: '8px',
                      textTransform: 'none', fontWeight: 600, fontSize: '0.78rem',
                      '&:hover': { borderColor: '#007f8f', bgcolor: 'rgba(0,151,169,0.04)' },
                    }}
                  >
                    {sampling.target ? 'Loading…' : 'Load target fields'}
                  </Button>
                </Stack>

                {(sampleInfo.source || sampleInfo.target) && (
                  <Stack direction="row" flexWrap="wrap" gap={0.75} mb={1.5}>
                    {sampleInfo.source && (
                      <Chip
                        size="small" icon={<Plane size={12} />} label={`Source: ${sampleInfo.source}`}
                        sx={{ bgcolor: '#eef2ff', color: '#01426A', fontWeight: 600, '& .MuiChip-icon': { color: '#01426A' } }}
                      />
                    )}
                    {sampleInfo.target && (
                      <Chip
                        size="small" icon={<Users size={12} />} label={`Target: ${sampleInfo.target}`}
                        sx={{ bgcolor: '#ecfeff', color: '#0097A9', fontWeight: 600, '& .MuiChip-icon': { color: '#0097A9' } }}
                      />
                    )}
                  </Stack>
                )}

                <Grid container spacing={1.5}>
                  {ADVANCED_FIELDS.map(([key, label, scope]) => (
                    <Grid size={{ xs: 12, sm: 6 }} key={key}>
                      <PathField
                        label={label}
                        value={fields[key]}
                        placeholder={DEFAULT_FIELDS[key] || 'reuse source'}
                        options={sampleOptions[scope]}
                        onChange={(val) => updateField(key, val)}
                      />
                    </Grid>
                  ))}
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      label="Target scan limit (messages)"
                      type="number"
                      value={targetScanLimit}
                      onChange={(e) => setTargetScanLimit(e.target.value)}
                      size="small"
                      fullWidth
                      inputProps={{ min: 1, max: 100000 }}
                      sx={inputSx}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Box>

          <Divider sx={{ my: 2.5 }} />

          <Button
            variant="contained"
            onClick={handleRun}
            disabled={loading || !source || !target}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Play size={16} />}
            sx={{
              textTransform: 'none', fontWeight: 700, borderRadius: '10px', px: 3,
              bgcolor: '#0097A9', '&:hover': { bgcolor: '#007f8f' },
            }}
          >
            {loading ? 'Comparing…' : 'Run comparison'}
          </Button>
        </Paper>
      </Fade>

      {error && (
        <Fade in>
          <Alert severity="error" sx={{ mb: 3, borderRadius: '12px' }}>
            {error}
          </Alert>
        </Fade>
      )}

      {/* Results */}
      {summary && (
        <Fade in timeout={400}>
          <Box>
            {/* Summary cards */}
            <Grid container spacing={2} mb={2.5}>
              <Grid size={{ xs: 6, md: 3 }}>
                <StatCard icon={Plane} label="Flights" value={summary.total_flights}
                  color="#01426A" bg="#eef2ff" />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <StatCard
                  icon={CheckCircle2}
                  label={summary.direction === 'crew_to_flight' ? 'Flight found' : 'With crew'}
                  value={summary.ok_count}
                  color="#00875A" bg="#ecfdf5" />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <StatCard
                  icon={AlertTriangle}
                  label={summary.direction === 'crew_to_flight' ? 'Flight missing' : 'Missing crew'}
                  value={summary.issue_count}
                  color="#dc2626" bg="#fef2f2" />
              </Grid>
              <Grid size={{ xs: 6, md: 3 }}>
                <StatCard icon={Users} label="Total crew" value={summary.total_crew}
                  color="#0097A9" bg="#ecfeff" />
              </Grid>
            </Grid>

            {/* Meta line */}
            <Paper elevation={0} sx={{ p: 2, mb: 2.5, borderRadius: '12px', border: '1px solid #e5e7eb' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}
                justifyContent="space-between">
                <Typography variant="caption" color="text.secondary">
                  Source <strong>{summary.source.name}</strong> ({summary.source.entity}
                  {summary.source.subscription ? ` / ${summary.source.subscription}` : ''},
                  {' '}{summary.source.queue_target}) · scanned {summary.source_scanned}
                  {summary.source_scan_capped && ' (capped)'}
                  {'  →  '}
                  Target <strong>{summary.target.name}</strong> ({summary.target.entity}
                  {summary.target.subscription ? ` / ${summary.target.subscription}` : ''},
                  {' '}{summary.target.queue_target}) · scanned {summary.target_scanned}
                  {summary.target_scan_capped && ' (capped)'}
                </Typography>
                <ToggleButtonGroup
                  value={filter} exclusive size="small" sx={toggleSx}
                  onChange={(_, v) => v && setFilter(v)}
                >
                  <ToggleButton value="all">All ({flights.length})</ToggleButton>
                  <ToggleButton value="issues">
                    <AlertTriangle size={13} style={{ marginRight: 5 }} />
                    Issues ({summary.issue_count})
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>
            </Paper>

            {results?.message && (
              <Alert severity="info" sx={{ mb: 2.5, borderRadius: '12px' }}>
                {results.message}
              </Alert>
            )}

            {/* Flight grid */}
            {visibleFlights.length === 0 ? (
              <Paper elevation={0} sx={{ p: 4, borderRadius: '14px', border: '1px dashed #cbd5e1', textAlign: 'center' }}>
                <Search size={26} color="#94a3b8" />
                <Typography variant="body2" color="text.secondary" mt={1}>
                  {filter === 'issues'
                    ? (summary.direction === 'crew_to_flight'
                        ? 'All crew flights have a matching source flight. '
                        : 'No flights are missing crew. ')
                    : 'No flights to display.'}
                </Typography>
              </Paper>
            ) : (
              <Grid container spacing={2}>
                {visibleFlights.map((f) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={`${f.carrier}-${f.flight_number}-${f.udor}`}>
                    <FlightCard flight={f} direction={summary.direction} />
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        </Fade>
      )}
    </Box>
  );
};

export default FlightCrewCompare;
